import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { PricingSummaryHeaderDetails } from '@sales-portal/domain/pricing/screens/management/components/pricing-summary-header-details';
import { PulsatingDot } from '@sales-portal/components/pulsating-dot';
import { toHoursAndMinutes } from '@/utils/date';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useExpirationCountdown } from '@sales-portal/blocs/hooks/use-expiration-countdown';
import { usePricingSummaryHeaderLayout } from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-pricing-summary-header-layout';
import { View } from 'react-native';

interface Props {
  costChangeImpact?: number;
  currencyCode: string;
  editsImpact?: number;
  restaurantCount: number;
  title: string;
  totalWeeklyVolume?: number;
  validUntil?: string;
  variantPackCount: number;
}

export const PricingSummaryHeader: React.FC<Props> = ({
  costChangeImpact,
  currencyCode,
  editsImpact,
  restaurantCount,
  title,
  totalWeeklyVolume,
  validUntil,
  variantPackCount,
}) => {
  const [styles, theme] = useStyles();
  const {
    containerStyle,
    handleLayout,
    headingVariant,
    isLarge,
    isStacked,
    wrapperStyle,
  } = usePricingSummaryHeaderLayout();

  const summaryCountdownTimeMargin = isLarge
    ? styles.summaryCountdownTimeMarginLarge
    : styles.summaryCountdownTimeMarginMedium;

  const millisecondsUntilPublish = useExpirationCountdown(validUntil);

  return (
    <View onLayout={handleLayout} style={containerStyle}>
      <View style={wrapperStyle}>
        <View collapsable={false}>
          <Typography variant="heading-lg">{title}</Typography>
          {millisecondsUntilPublish !== null ? (
            <View
              style={[styles.summaryCountdownTime, summaryCountdownTimeMargin]}
            >
              <PulsatingDot color={theme.sk.color.text.warning} />
              <Typography
                color={theme.sk.color.text.warning}
                variant="paragraph-sm-600"
              >
                {i18n.t(
                  'pricing.summary.duration.hours_minutes.left_to_publish',
                  toHoursAndMinutes(millisecondsUntilPublish),
                )}
              </Typography>
            </View>
          ) : null}
        </View>
        <PricingSummaryHeaderDetails
          costChangeImpact={costChangeImpact}
          currencyCode={currencyCode}
          editsImpact={editsImpact}
          headingVariant={headingVariant}
          isStacked={isStacked}
          restaurantCount={restaurantCount}
          totalWeeklyVolume={totalWeeklyVolume}
          variantPackCount={variantPackCount}
        />
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  summaryCountdownTime: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: theme.sk.color.bg.warning,
    borderRadius: theme.sk.radii.radiusLarge,
    flexDirection: 'row',
    gap: 6,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceXsmall,
  },
  summaryCountdownTimeMarginLarge: {
    marginTop: theme.sk.spacing.spaceMedium,
  },
  summaryCountdownTimeMarginMedium: {
    marginTop: theme.sk.spacing.spaceSmall,
  },
}));
